/**
 * Formatting utility functions for display values.
 * These are distinct from format.ts which handles number formatting (currency, percentage).
 */

/**
 * Convert SNAKE_CASE or kebab-case enum values to Title Case.
 * @example formatEnumValue('IN_PROGRESS') => 'In Progress'
 * @example formatEnumValue('completed') => 'Completed'
 */
export function formatEnumValue(value: string): string {
  return value
    .toLowerCase()
    .replace(/_/g, ' ')
    .replace(/-/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase())
}

/**
 * Convert camelCase or PascalCase field names to human-readable format.
 * @example formatFieldName('companyName') => 'Company Name'
 * @example formatFieldName('externalId') => 'External Id'
 */
export function formatFieldName(fieldName: string): string {
  return fieldName
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (str) => str.toUpperCase())
    .trim()
}

/**
 * Format a dotted field path for display in sort/filter labels.
 * Extracts the meaningful segment and formats it nicely.
 * Handles both dot notation (location.city.name) and double underscore notation (Location__city__name).
 * @example formatSortFieldName('location.city.name') => 'City'
 * @example formatSortFieldName('Location__city__name') => 'City'
 * @example formatSortFieldName('primaryOwner.name') => 'Primary Owner'
 * @example formatSortFieldName('companyName') => 'Company Name'
 */
export function formatSortFieldName(fieldName: string): string {
  // Normalize double underscores to dots for consistent processing
  const normalized = fieldName.replace(/__/g, '.')

  if (!normalized.includes('.')) {
    return formatFieldName(normalized)
  }

  const parts = normalized.split('.')
  // If last segment is 'name' or 'id', use the segment before it (when available)
  // Otherwise use the last segment
  const lastSegment = parts[parts.length - 1]
  const meaningfulSegment =
    parts.length >= 2 && (lastSegment === 'name' || lastSegment === 'id')
      ? parts[parts.length - 2]
      : lastSegment

  return formatFieldName(meaningfulSegment)
}

interface Location {
  city?: { name?: string } | null
  state?: { name?: string } | null
  country?: { name?: string } | null
}

/**
 * Format a location object into a display string.
 * Shows city, state, and country (only country if no state).
 * @example formatLocationName({ city: { name: 'NYC' }, state: { name: 'NY' } }) => 'NYC, NY'
 */
export function formatLocationName(location: Location): string {
  const parts: string[] = []
  if (location.city?.name) parts.push(location.city.name)
  if (location.state?.name) parts.push(location.state.name)
  if (location.country?.name && !location.state)
    parts.push(location.country.name)
  return parts.join(', ') || 'Unknown'
}

/**
 * Format a location object for display in cells.
 * Similar to formatLocationName but returns empty string instead of 'Unknown'.
 * @example formatLocationDisplay({ city: { name: 'NYC' }, state: { name: 'NY' } }) => 'NYC, NY'
 */
export function formatLocationDisplay(location: Location): string {
  const parts: string[] = []
  if (location.city?.name) parts.push(location.city.name)
  if (location.state?.name) parts.push(location.state.name)
  if (!location.state && location.country?.name)
    parts.push(location.country.name)
  return parts.join(', ')
}
